// What a guest may type between the digits: spaces, hyphens, brackets, slashes, dots.
// Everything left after these have gone has to be a digit, or the leading "+".
const SEPARATORS = /[\s\-()./]/g;

// The mobile prefixes, and the "21" that behaves like one. Each takes 7 digits after it.
const MOBILE_PREFIX = "20|21|30|31|50|70";

// A Hungarian number with no country or trunk prefix left on it: a mobile (9 digits), a
// Budapest line ("1" + 7), or any other area code (2 digits + 6). The lookahead stops a mobile
// prefix from passing as a short landline — "20 123 456" is not a number.
const HUNGARIAN_NUMBER = new RegExp(
  `^(?:(?:${MOBILE_PREFIX})\\d{7}|1\\d{7}|(?!${MOBILE_PREFIX})[2-9]\\d{7})$`
);

// How the country and trunk prefixes are written when they are unmistakable.
const HUNGARIAN_PREFIX = /^(?:\+36|0036|06)/;

// A bare "36" is the ambiguous one: the country code with its "+" left off, or Eger's own area
// code. Length tells them apart, so it is only read as the country code when the number does
// not already read as a whole one on its own — see `isValidPhoneNumber`.
const BARE_COUNTRY_CODE = /^36/;

// Anything dialled from another country: E.164 allows 15 digits at most, and nothing real is
// shorter than 8 with its country code. Deliberately loose — a foreign guest's number is not
// ours to second-guess, and refusing it would stop them booking.
const INTERNATIONAL_NUMBER = /^\+(?!36)\d{8,15}$/;

/** The number with every separator taken out, so only digits and a leading "+" are left. */
export const normalizePhoneNumber = (value) => String(value ?? "").replace(SEPARATORS, "");

/**
 * Whether a guest could be reached on what they typed.
 *
 * Written for what a person writes down rather than for what the database stores:
 * "+36 30 123 4567", "06301234567", "36301234567" and "(06) 30/123-4567" are one number and all
 * pass. A Hungarian number is checked properly — prefix, mobile or area code, and the number of
 * digits that code takes; a foreign one only has to look like a number at all.
 */
export const isValidPhoneNumber = (value) => {
  const normalized = normalizePhoneNumber(value);

  if (INTERNATIONAL_NUMBER.test(normalized)) return true;

  const withoutPrefix = normalized.replace(HUNGARIAN_PREFIX, "");

  // The whole number first, the bare country code only as a fallback. That order is what keeps
  // both readings of a leading "36": "36 123 456" is a whole number already — the Eger line —
  // while "36 30 123 1234" is not one, and drops its "36" to become the mobile it is.
  return HUNGARIAN_NUMBER.test(withoutPrefix)
    || HUNGARIAN_NUMBER.test(withoutPrefix.replace(BARE_COUNTRY_CODE, ""));
};
